/**
 * Quartiles with their working, for the quartile and percentile calculators.
 *
 * The numbers themselves come from `quantile` and `fiveNumberSummary` in
 * `@/lib/stats/descriptive` (tested against SciPy). This module only explains them: the
 * position each method looks up, the two neighbours it interpolates between, and — for
 * the median-of-halves method — the halves themselves.
 */

import {
  type FiveNumberSummary,
  fiveNumberSummary,
  type QuantileMethod,
  quantile,
  sorted,
} from "@/lib/stats/descriptive";

/** One interpolated lookup: "position 3.25 → 15 + 0.25 × (36 − 15)". */
export interface QuantileStep {
  /** The probability looked up, 0–1. */
  p: number;
  /** 1-based position in the sorted data; may be fractional. */
  position: number;
  /** 1-based index of the lower neighbour after clamping to the data. */
  lowerRank: number;
  lowerValue: number;
  upperValue: number;
  /** How far between the neighbours, 0–1. */
  fraction: number;
  value: number;
  /** True when the position fell outside 1…n and the nearest end value was used. */
  clamped: boolean;
}

/** 1-based position of the p-quantile under each interpolating method. */
export function quantilePosition(
  n: number,
  p: number,
  method: Exclude<QuantileMethod, "tukey">
): number {
  return method === "exclusive" ? (n + 1) * p : 1 + (n - 1) * p;
}

/**
 * The lookup behind `quantile(values, p, method)`, step by step.
 *
 * `data` must be sorted. The arithmetic mirrors `quantile` exactly — the same 0-based
 * position, the same floor, the same clamping — so the working shown can never
 * disagree with the value it explains.
 */
export function quantileStep(
  data: readonly number[],
  p: number,
  method: Exclude<QuantileMethod, "tukey">
): QuantileStep {
  const n = data.length;
  const zeroBased = method === "exclusive" ? (n + 1) * p - 1 : (n - 1) * p;
  const lower = Math.floor(zeroBased);
  const value = quantile(data, p, method);
  const base = { p, position: zeroBased + 1, value };
  if (lower + 1 >= n) {
    const last = data.at(-1) as number;
    return {
      ...base,
      lowerRank: n,
      lowerValue: last,
      upperValue: last,
      fraction: 0,
      clamped: zeroBased > n - 1,
    };
  }
  if (lower < 0) {
    return {
      ...base,
      lowerRank: 1,
      lowerValue: data[0],
      upperValue: data[0],
      fraction: 0,
      clamped: true,
    };
  }
  return {
    ...base,
    lowerRank: lower + 1,
    lowerValue: data[lower],
    upperValue: data[lower + 1],
    fraction: zeroBased - lower,
    clamped: false,
  };
}

export interface TukeyHalves {
  lower: number[];
  upper: number[];
  /** n is odd, so the overall median belongs to neither half. */
  medianExcluded: boolean;
}

/** The two halves the median-of-halves method takes medians of. */
export function tukeyHalves(data: readonly number[]): TukeyHalves {
  const n = data.length;
  const half = Math.floor(n / 2);
  return {
    lower: data.slice(0, half),
    upper: data.slice(n % 2 === 0 ? half : half + 1),
    medianExcluded: n % 2 === 1,
  };
}

export interface QuartileReport {
  /** The data, sorted ascending. */
  data: number[];
  n: number;
  method: QuantileMethod;
  summary: FiveNumberSummary;
  /** Box-plot whisker ends: the most extreme values inside the fences. */
  whiskerLow: number;
  whiskerHigh: number;
  /** Q1, median and Q3 lookups — interpolating methods only. */
  steps: QuantileStep[] | null;
  /** The halves — the median-of-halves method only. */
  halves: TukeyHalves | null;
}

export const QUARTILE_PROBABILITIES = [0.25, 0.5, 0.75] as const;

export function quartileReport(
  values: readonly number[],
  method: QuantileMethod
): QuartileReport | null {
  if (values.length === 0) {
    return null;
  }
  const data = sorted(values);
  const summary = fiveNumberSummary(data, method);
  const inside = data.filter(
    (value) => value >= summary.lowerFence && value <= summary.upperFence
  );
  return {
    data,
    n: data.length,
    method,
    summary,
    whiskerLow: inside[0] ?? summary.min,
    whiskerHigh: inside.at(-1) ?? summary.max,
    steps:
      method === "tukey"
        ? null
        : QUARTILE_PROBABILITIES.map((p) => quantileStep(data, p, method)),
    halves: method === "tukey" ? tukeyHalves(data) : null,
  };
}
